import { useCallback, useEffect } from "react";
import { Role, CreateRole, UpdateRole } from "../types/models/Role.d";
import { UpdateEntityRole } from "../types/models/Role";
import { useGetRoleListQuery, useInsertRoleMutation, useUpdateRoleMutation, useDeleteRoleMutation, } from "../apis/Role.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useRole(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertRoleMutation, { 
        isLoading: insertRoleLoading, 
        isSuccess: insertRoleIsSuccess,
        isError: insertRoleIsError,
        error: insertRoleError,
    }] = useInsertRoleMutation()
    const [updateRoleMutation, { 
        isLoading: updateRoleLoading,
        isSuccess: updateRoleIsSuccess,
        isError: updateRoleIsError,
        error: updateRoleError,
    }] = useUpdateRoleMutation()
    const [deleteRoleMutation, { 
        isLoading: deleteRoleLoading,
        isSuccess: deleteRoleIsSuccess,
        isError: deleteRoleIsError,
        error: deleteRoleError,
    }] = useDeleteRoleMutation()

    const loading =
        insertRoleLoading ||
        updateRoleLoading ||
        deleteRoleLoading ||
        false

    const insertRole = useCallback(async (role: CreateRole, redirectPath: string|undefined) => {
        try {
            const data = await insertRoleMutation(role).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertRoleMutation, navigate])
    
    useEffect(() => {
        if (insertRoleIsSuccess) {
            openSuccessAlert('Role created')
        } else if (insertRoleIsError) {
            console.log("add error", insertRoleError)
            openFailAlert(t`Role add error: ` + insertRoleError?.data?.message)
        }
    }, [
        insertRoleIsSuccess,
        insertRoleIsError,
        insertRoleError
    ]);

    const updateRole = useCallback(async (role: UpdateEntityRole, redirectPath: string|undefined) => {
        try {
            const data = await updateRoleMutation(role).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateRoleMutation, navigate])
    
    useEffect(() => {
        if (updateRoleIsSuccess) {
            openSuccessAlert(t`Role updated`)
        } else if (updateRoleIsError) {
            console.log("update error", updateRoleError)
            openFailAlert(t`Role update error: ` + updateRoleError?.data?.message)
        }
    }, [
        updateRoleIsSuccess,
        updateRoleIsError,
        updateRoleError
    ]);

    const deleteRole = useCallback((role: Role, redirectPath: string|undefined) => {
        openDialog(async () => {
            role = {
                ...role,
            }
            try {
                const { data } = await deleteRoleMutation(role);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteRoleMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteRoleIsSuccess) {
            openSuccessAlert(t`Role deleted`)
        } else if (deleteRoleIsError) {
            console.log("delete error", deleteRoleError)
            openFailAlert(t`Role delete error: ` + deleteRoleError?.data?.message)
        }
    }, [
        deleteRoleIsSuccess,
        deleteRoleIsError,
        deleteRoleError
    ]);

    return {
        loading,
        insertRole,
        updateRole,
        deleteRole
    }
}
